import { useState, useEffect, useCallback, useRef } from 'react';
import { Thread, Message } from '../types/thread';
import { threadService } from '../services/threadService';
import { useAuth } from './useAuth';
import { useNotification } from './useNotification';

interface UseThreadsReturn {
  threads: Thread[];
  loading: boolean;
  error: string | null;
  totalUnreadCount: number;
  refreshThreads: () => Promise<void>;
  markThreadRead: (threadId: string) => void;
}

/**
 * Custom hook for managing threads with real-time updates
 */
export const useThreads = (): UseThreadsReturn => {
  const { profile: memberProfile } = useAuth();
  const { showInfo } = useNotification();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const previousThreadsRef = useRef<Thread[]>([]);

  // Calculate total unread count: exact counts for participant threads plus
  // one per unread audience thread (those only carry a "new" dot).
  const calculateUnreadCount = useCallback((threadsList: Thread[]) => {
    const total = threadsList.reduce(
      (sum, thread) => sum + (thread.unreadCount || (thread.hasUnread ? 1 : 0)),
      0,
    );
    setTotalUnreadCount(total);
  }, []);

  // Mark a thread read: optimistic local clear + persist per-member read state
  const markThreadRead = useCallback(
    (threadId: string) => {
      setThreads(prev => {
        const next = prev.map(t =>
          t.id === threadId ? { ...t, unreadCount: 0, hasUnread: false } : t,
        );
        calculateUnreadCount(next);
        return next;
      });
      threadService.markThreadRead(threadId);
    },
    [calculateUnreadCount],
  );

  // Check for new messages and show notifications
  const checkForNewMessages = useCallback(
    (newThreads: Thread[]) => {
      if (previousThreadsRef.current.length === 0) {
        // First load, don't show notifications
        previousThreadsRef.current = newThreads;
        return;
      }

      newThreads.forEach(newThread => {
        const oldThread = previousThreadsRef.current.find(t => t.id === newThread.id);

        // Check if there's a new message
        if (
          newThread.lastMessage &&
          (!oldThread?.lastMessage ||
            newThread.lastMessage.timestamp.getTime() > oldThread.lastMessage.timestamp.getTime())
        ) {
          // Don't show notification if the current user sent the message
          if (memberProfile && newThread.lastMessage.senderId !== memberProfile.id) {
            const threadName =
              newThread.type === 'direct' ? newThread.lastMessage.senderName : newThread.name;
            showInfo(
              `New message in ${threadName}: ${newThread.lastMessage.content.substring(0, 50)}${newThread.lastMessage.content.length > 50 ? '...' : ''}`,
              4000,
            );
          }
        }
      });

      previousThreadsRef.current = newThreads;
    },
    [memberProfile, showInfo],
  );

  // Refresh threads manually
  const refreshThreads = useCallback(async () => {
    if (!memberProfile) return;

    try {
      setLoading(true);
      setError(null);
      const fetchedThreads = await threadService.getThreads(memberProfile);
      setThreads(fetchedThreads);
      calculateUnreadCount(fetchedThreads);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch threads';
      setError(errorMessage);
      console.error('Error refreshing threads:', err);
    } finally {
      setLoading(false);
    }
  }, [memberProfile, calculateUnreadCount]);

  // Set up real-time listener
  useEffect(() => {
    console.log('🔍 useThreads: Effect triggered, memberProfile:', memberProfile ? `${memberProfile.displayName} (${memberProfile.id})` : 'NULL');
    
    if (!memberProfile) {
      console.log('⚠️  useThreads: No member profile, skipping thread loading');
      setLoading(false);
      return;
    }

    console.log('✅ useThreads: Member profile exists, entity memberships:', memberProfile.entityMemberships?.length || 0);
    
    setLoading(true);
    setError(null);

    // Subscribe to real-time updates (passing full memberProfile for entity filtering)
    const unsubscribe = threadService.subscribeToThreads(memberProfile, updatedThreads => {
      
      // Threads are already filtered by subscribeToThreads
      setThreads(updatedThreads);
      calculateUnreadCount(updatedThreads);
      checkForNewMessages(updatedThreads);
      setLoading(false);
    });

    unsubscribeRef.current = unsubscribe;

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribeRef.current) {
        
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [memberProfile, calculateUnreadCount, checkForNewMessages]);

  return {
    threads,
    loading,
    error,
    totalUnreadCount,
    refreshThreads,
    markThreadRead,
  };
};

interface UseThreadMessagesReturn {
  messages: Message[];
  loading: boolean;
  error: string | null;
  sendMessage: (content: string, attachments?: any[]) => Promise<void>;
  markAsRead: () => Promise<void>;
}

/**
 * Custom hook for managing messages in a specific thread with real-time updates
 */
export const useThreadMessages = (threadId: string): UseThreadMessagesReturn => {
  const { profile: memberProfile } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Send a message
  const sendMessage = useCallback(
    async (content: string, attachments?: any[]) => {
      if (!memberProfile || !content.trim()) return;

      try {
        // Extract first attachment if provided (for backwards compatibility)
        const attachment = attachments && attachments.length > 0 ? attachments[0] : undefined;
        
        await threadService.sendMessage(
          threadId,
          memberProfile.id,
          memberProfile.displayName,
          content,
          memberProfile.profileImageUrl,
          undefined, // Placeholder for backwards compatibility
          attachment?.url,
          attachment?.type,
        );
      } catch (err) {
        console.error('Error sending message:', err);
        throw err;
      }
    },
    [threadId, memberProfile],
  );

  // Mark all messages as read
  const markAsRead = useCallback(async () => {
    if (!memberProfile) return;

    try {
      await threadService.markThreadAsRead(threadId, memberProfile.id);
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [threadId, memberProfile]);

  // Set up real-time listener for messages
  useEffect(() => {
    if (!threadId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Subscribe to real-time message updates
    const unsubscribe = threadService.subscribeToThread(threadId, updatedMessages => {
      setMessages(updatedMessages);
      setLoading(false);
    });

    unsubscribeRef.current = unsubscribe;

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [threadId]);

  return {
    messages,
    loading,
    error,
    sendMessage,
    markAsRead,
  };
};
